import { motion, AnimatePresence, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { useState, useEffect, useRef, type CSSProperties } from "react";
import { Link } from "@tanstack/react-router";
import { Navbar, Footer } from "@/components/site-chrome";
import { Button } from "@/components/ui/button";
import {
  ReceiptIcon, TransportIcon, BookIcon, MealIcon,
  CheckIcon, WalletIcon, MessageSquareIcon,
  ArrowRightIcon, ShieldIcon, SparkleIcon,
} from "@/components/ui/TriIcons";

const EASE = [0.32, 0.72, 0, 1] as const;

// Editorial palette — warm cream, wine-red, charcoal
const WINE = "#7B1416";
const WINE_L = "#A42022";
const GOLD = "#C4A882"; // warm sand accent
const INK = "#1C1917"; // charcoal
const INK_SOFT = "#2A2522";
const PAPER = "#F5EFE4"; // warm cream
const STONE = "#EDE4D4"; // deeper cream
const STONE_DEEP = "#E2D6C0";
const HAIR = "rgba(28,25,23,0.10)";
const FONT = "'Georgia', 'Times New Roman', 'Playfair Display', serif";
const FONT_SANS = "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', system-ui, sans-serif";

const notifications = [
  { id: "1", icon: ReceiptIcon, title: "School fees received", body: "UGX 850,000 paid to Kampala International School", time: "now", color: INK },
  { id: "2", icon: TransportIcon, title: "Bus arriving in 4 minutes", body: "Bus 12 is 1.2km away. Sarah will be picked up at 7:42 AM.", time: "2m", color: GOLD },
  { id: "3", icon: BookIcon, title: "Sarah scored 92% in Mathematics", body: "Term 2 midterm results published. Above class average.", time: "1h", color: INK },
  { id: "4", icon: MealIcon, title: "Lunch purchased", body: "UGX 8,000 — Posho, beans, and fruit. Wallet balance: UGX 42,000", time: "3h", color: GOLD },
  { id: "5", icon: CheckIcon, title: "Attendance confirmed", body: "Sarah checked in at 7:48 AM. Gate 2 — Biometric verified.", time: "5h", color: INK },
  { id: "6", icon: MessageSquareIcon, title: "Message from Mr. Okello", body: "Sarah is doing excellently in science. Recommend the advanced lab program.", time: "1d", color: GOLD },
];

const features = [
  { icon: ReceiptIcon, title: "Pay fees instantly", desc: "Mobile money, card, or wallet — receipts generated automatically", color: INK },
  { icon: TransportIcon, title: "Track the bus live", desc: "Know exactly when your child boards and arrives — with geofencing alerts", color: GOLD },
  { icon: BookIcon, title: "See academic progress", desc: "Grades, attendance, and teacher feedback in real time", color: INK },
  { icon: WalletIcon, title: "Control spending", desc: "Sub-wallets for meals, books, and transport — with spending limits", color: GOLD },
  { icon: MessageSquareIcon, title: "Message teachers directly", desc: "No more phone calls to the office. Chat in-app or via WhatsApp", color: INK },
  { icon: ShieldIcon, title: "Verify identity", desc: "Biometric check-in ensures your child is safe at school", color: GOLD },
];

const steps = [
  { n: "01", title: "Create your account", desc: "Sign up as a parent and add your children in seconds.", color: INK },
  { n: "02", title: "Top up your wallet", desc: "Send Mobile Money to your parent wallet — one charge, one reference.", color: GOLD },
  { n: "03", title: "Assign funds", desc: "Split the top-up across fees, transport, meals, uniforms — per child.", color: INK },
  { n: "04", title: "Track everything", desc: "See every transaction, every notification, in real time.", color: GOLD },
];

// Dot grid pattern — like the image
const DOT_GRID = "radial-gradient(circle, rgba(28,25,23,0.06) 1px, transparent 1px)";
const DOT_GRID_GOLD = "radial-gradient(circle, rgba(196,168,130,0.16) 1px, transparent 1px)";

// ═══════════════════════════════════════════════════════════════
// Reveal
// ═══════════════════════════════════════════════════════════════
function Reveal({ children, delay = 0, y = 24 }: { children: React.ReactNode; delay?: number; y?: number }) {
  return (
    <motion.div initial={{ opacity: 0, y }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }} transition={{ delay, duration: 0.7, ease: EASE }}>
      {children}
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════
// GoldCorner — decorative gold swoosh like the image
// ═══════════════════════════════════════════════════════════════
function GoldCorner({ position }: { position: "tl" | "tr" | "bl" | "br" }) {
  const pos = {
    tl: { top: 0, left: 0, rotate: "0deg" as const },
    tr: { top: 0, right: 0, rotate: "90deg" as const },
    bl: { bottom: 0, left: 0, rotate: "-90deg" as const },
    br: { bottom: 0, right: 0, rotate: "180deg" as const },
  }[position];
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" className="absolute" style={pos}>
      <path d="M 2 2 L 2 28 M 2 2 L 28 2" stroke={WINE} strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <circle cx="2" cy="2" r="2.5" fill={WINE} />
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════
// Eyebrow — gold label with lines
// ═══════════════════════════════════════════════════════════════
function Eyebrow({ children, dark }: { children: React.ReactNode; dark?: boolean }) {
  return (
    <Reveal>
      <div className="flex items-center gap-4">
        <div className="h-px w-10" style={{ background: dark ? "rgba(255,255,255,0.2)" : GOLD }} />
        <span className="text-[11px] font-bold uppercase tracking-[0.28em]" style={{ color: dark ? "rgba(255,255,255,0.5)" : GOLD, fontFamily: FONT }}>
          {children}
        </span>
      </div>
    </Reveal>
  );
}

export function ParentsPage() {
  return (
    <div className="relative min-h-screen overflow-hidden" style={{ background: PAPER, fontFamily: FONT_SANS }}>
      {/* Grain texture */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.035]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`, backgroundSize: "200px 200px" }} />
      {/* Top ruled band */}
      <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: `linear-gradient(90deg, ${WINE}, ${GOLD}, ${WINE})` }} />
      <div className="relative z-10">
      <Navbar />
      <ParentsHero />
      <FeaturesSection />
      <HowItWorksSection />
      <CTASection />
      <Footer />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// HERO — pure white, jet-black headline, gold accents, phone mockup
// ═══════════════════════════════════════════════════════════════
function ParentsHero() {
  const [visibleCount, setVisibleCount] = useState(1);
  const [walletBalance, setWalletBalance] = useState(42000);
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, -50]);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const smoothX = useSpring(mouseX, { stiffness: 40, damping: 20 });
  const smoothY = useSpring(mouseY, { stiffness: 40, damping: 20 });
  const rotateX = useTransform(smoothY, [-0.5, 0.5], [4, -4]);
  const rotateY = useTransform(smoothX, [-0.5, 0.5], [-4, 4]);

  useEffect(() => {
    const id = setInterval(() => {
      setVisibleCount((p) => {
        if (p >= notifications.length) { setTimeout(() => setVisibleCount(1), 3000); return p; }
        return p + 1;
      });
    }, 2400);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (visibleCount === 1) setWalletBalance(42000);
    else if (visibleCount >= 4) setWalletBalance(34000);
  }, [visibleCount]);

  function onmousemove(e: React.MouseEvent) {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    mouseX.set((e.clientX - rect.left) / rect.width - 0.5);
    mouseY.set((e.clientY - rect.top) / rect.height - 0.5);
  }

  return (
    <section ref={containerRef} className="relative overflow-hidden pt-32 pb-20 sm:pt-36 sm:pb-28" onMouseMove={onmousemove}
      style={{ perspective: 1200, background: PAPER } as CSSProperties}>
      {/* Dot grid background */}
      <div className="absolute inset-0" style={{ backgroundImage: DOT_GRID, backgroundSize: "24px 24px", opacity: 0.6 }} />
      {/* Subtle gold radial glow */}
      <motion.div className="pointer-events-none absolute" style={{ left: "60%", top: "20%", width: 500, height: 500, background: `radial-gradient(circle, ${GOLD}08 0%, transparent 60%)`, filter: "blur(60px)" }}
        animate={{ opacity: [0.3, 0.6, 0.3] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }} />

      <motion.div className="relative z-10 mx-auto max-w-6xl px-6 sm:px-8" style={{ y: heroY }}>
        <div className="grid items-center gap-12 lg:grid-cols-[1fr_1fr] lg:gap-16">
          {/* Left — copy */}
          <div>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} className="mb-8 flex items-center gap-4">
              <div className="h-px w-12" style={{ background: WINE }} />
              <span className="text-[11px] font-bold uppercase tracking-[0.28em]" style={{ color: WINE, fontFamily: FONT_SANS }}>For Parents</span>
            </motion.div>

            <h1 className="text-[2.75rem] font-black leading-[1.02] tracking-[-0.035em] sm:text-6xl lg:text-[4.25rem]" style={{ fontFamily: FONT, color: INK }}>
              <motion.span initial={{ opacity: 0, y: 30, filter: "blur(10px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                transition={{ delay: 0.1, duration: 0.7, ease: EASE }} className="block">Every moment of</motion.span>
              <motion.span initial={{ opacity: 0, y: 30, filter: "blur(10px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                transition={{ delay: 0.2, duration: 0.7, ease: EASE }} className="block">your child's</motion.span>
              <motion.span initial={{ opacity: 0, y: 30, filter: "blur(10px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                transition={{ delay: 0.3, duration: 0.7, ease: EASE }} className="block" style={{ color: WINE, fontStyle: "italic" }}>education.</motion.span>
            </h1>

            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.6, ease: EASE }}
              className="mt-7 max-w-md text-base leading-[1.6] font-medium sm:text-lg" style={{ color: "#555", fontFamily: FONT }}>
              No more backpack notes. No more phone calls to the office. No more wondering
              where the bus is. One app connects you to every aspect of your child's school day — in real time.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65, duration: 0.6, ease: EASE }} className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg" className="h-12 rounded-none px-7 text-[13px] font-bold uppercase tracking-[0.15em] transition-all hover:opacity-90" style={{ background: WINE, color: "#fff", border: "none", fontFamily: FONT_SANS }}>
                <Link to="/auth">Get Started <ArrowRightIcon className="ml-2 h-4 w-4" accent="#fff" style={{ color: "#fff" }} /></Link>
              </Button>
              <Button asChild variant="ghost" size="lg" className="h-12 rounded-none px-6 text-[13px] font-bold uppercase tracking-[0.15em] hover:bg-transparent" style={{ color: INK, border: `1.5px solid ${INK}30`, fontFamily: FONT_SANS }}>
                <Link to="/auth">Sign In</Link>
              </Button>
            </motion.div>
          </div>

          {/* Right — phone mockup */}
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.8, ease: EASE }} className="flex justify-center" style={{ transformStyle: "preserve-3d" }}>
            <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}>
              <div className="relative overflow-hidden rounded-[2.5rem] border-[6px] shadow-2xl" style={{ width: 300, height: 600, borderColor: INK, background: PAPER, boxShadow: "0 30px 80px -20px rgba(123,20,22,0.18), 0 8px 32px -8px rgba(0,0,0,0.08)" }}>
                <div className="flex items-center justify-between px-5 pt-3 pb-1">
                  <span className="text-xs font-bold" style={{ color: INK, fontFamily: FONT }}>9:41</span>
                  <div className="flex items-center gap-1">
                    <div className="h-2 w-4 rounded-sm" style={{ background: `${INK}25` }} />
                    <div className="h-2 w-3 rounded-sm" style={{ background: `${INK}25` }} />
                    <div className="h-3 w-4 rounded-sm" style={{ background: `${INK}80` }} />
                  </div>
                </div>
                <div className="absolute left-1/2 top-0 h-6 w-28 -translate-x-1/2 rounded-b-2xl" style={{ background: INK }} />
                <div className="px-4 pb-4 pt-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-semibold" style={{ color: "#999", fontFamily: FONT }}>Good morning,</p>
                      <p className="text-base font-bold" style={{ color: INK, fontFamily: FONT, letterSpacing: "-0.02em" }}>Sarah's Parent</p>
                    </div>
                    <div className="grid h-8 w-8 place-items-center rounded-full" style={{ background: `${GOLD}15` }}>
                      <WalletIcon className="h-4 w-4" accent={GOLD} style={{ color: GOLD }} />
                    </div>
                  </div>

                  <motion.div animate={{ scale: [1, 1.015, 1] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    className="mt-3 overflow-hidden rounded-2xl p-4" style={{ background: WINE, boxShadow: `0 8px 24px ${WINE}30` }}>
                    <p className="text-[9px] font-bold uppercase tracking-wider" style={{ color: `${GOLD}CC`, fontFamily: FONT_SANS }}>Wallet Balance</p>
                    <motion.span key={walletBalance} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                      className="mt-1 block text-xl font-black text-white" style={{ fontFamily: FONT, letterSpacing: "-0.03em" }}>
                      UGX {walletBalance.toLocaleString()}
                    </motion.span>
                    <div className="mt-3 flex items-center gap-2">
                      <div className="h-1 flex-1 overflow-hidden rounded-full" style={{ background: "rgba(255,255,255,0.1)" }}>
                        <motion.div className="h-full rounded-full" style={{ background: GOLD }} animate={{ width: `${(walletBalance / 50000) * 100}%` }} transition={{ duration: 0.5 }} />
                      </div>
                      <span className="text-[9px] font-bold" style={{ color: "rgba(255,255,255,0.4)", fontFamily: FONT }}>{Math.round((walletBalance / 50000) * 100)}%</span>
                    </div>
                  </motion.div>

                  <div className="mt-3">
                    <p className="mb-2 text-[9px] font-bold uppercase tracking-wider" style={{ color: "#999", fontFamily: FONT }}>Today's Activity</p>
                    <div className="space-y-1.5">
                      <AnimatePresence>
                        {notifications.slice(0, visibleCount).map((n) => {
                          const NIcon = n.icon;
                          return (
                            <motion.div key={n.id} initial={{ opacity: 0, y: 16, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, x: -16 }}
                              transition={{ duration: 0.35, ease: EASE }} className="flex items-start gap-2.5 rounded-xl border bg-white p-2.5"
                              style={{ borderColor: HAIR, boxShadow: "0 1px 4px rgba(0,0,0,0.02)" }}>
                              <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg" style={{ background: `${n.color}10` }}>
                                <NIcon className="h-3.5 w-3.5" accent={n.color} style={{ color: n.color }} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-[11px] font-bold" style={{ color: INK, fontFamily: FONT }}>{n.title}</p>
                                <p className="mt-0.5 text-[10px] leading-snug font-medium" style={{ color: "#888", fontFamily: FONT }}>{n.body}</p>
                              </div>
                              <span className="text-[8px] font-semibold" style={{ color: "#bbb", fontFamily: FONT }}>{n.time}</span>
                            </motion.div>
                          );
                        })}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
                <div className="absolute bottom-1 left-1/2 h-1 w-20 -translate-x-1/2 rounded-full" style={{ background: `${INK}15` }} />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════
// FEATURES — white cards on stone background, gold accents
// ═══════════════════════════════════════════════════════════════
function FeaturesSection() {
  return (
    <section className="relative py-24 sm:py-32" style={{ background: STONE }}>
      <div className="absolute inset-0" style={{ backgroundImage: DOT_GRID, backgroundSize: "24px 24px", opacity: 0.4 }} />
      <div className="relative mx-auto max-w-6xl px-6 sm:px-8">
        <Eyebrow>Features</Eyebrow>
        <Reveal delay={0.1}>
          <h2 className="mt-6 text-4xl font-black tracking-[-0.035em] sm:text-5xl lg:text-6xl" style={{ color: INK, fontFamily: FONT }}>
            Everything parents need.
          </h2>
          <p className="mt-3 text-2xl font-bold tracking-[-0.02em] sm:text-3xl" style={{ color: GOLD, fontFamily: FONT }}>
            Nothing they don't.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border sm:grid-cols-2 lg:grid-cols-3" style={{ borderColor: HAIR, background: HAIR }}>
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.06, duration: 0.5 }}
              whileHover={{ y: -4 }} className="group relative bg-white p-7 transition-colors hover:bg-stone-50">
              <motion.div className="grid h-11 w-11 place-items-center rounded-xl" style={{ background: `${f.color}08` }}
                whileHover={{ scale: 1.1 }} transition={{ type: "spring", stiffness: 300, damping: 20 }}>
                <f.icon className="h-5 w-5" accent={f.color} style={{ color: f.color }} />
              </motion.div>
              <h3 className="mt-4 text-lg font-bold" style={{ color: INK, fontFamily: FONT, letterSpacing: "-0.02em" }}>{f.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed font-medium" style={{ color: "#666", fontFamily: FONT }}>{f.desc}</p>
              <div className="absolute bottom-0 left-0 h-0.5 w-0 transition-all duration-300 group-hover:w-full" style={{ background: GOLD }} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════
// HOW IT WORKS — pure white, big gold numbers, editorial
// ═══════════════════════════════════════════════════════════════
function HowItWorksSection() {
  return (
    <section className="relative py-24 sm:py-32" style={{ background: PAPER }}>
      <div className="absolute inset-0" style={{ backgroundImage: DOT_GRID_GOLD, backgroundSize: "28px 28px", opacity: 0.3 }} />
      <div className="relative mx-auto max-w-5xl px-6 sm:px-8">
        <Eyebrow>How It Works</Eyebrow>
        <Reveal delay={0.1}>
          <h2 className="mt-6 text-4xl font-black tracking-[-0.035em] sm:text-5xl lg:text-6xl" style={{ color: INK, fontFamily: FONT }}>
            From sign up to tracking
          </h2>
          <p className="mt-2 text-2xl font-bold tracking-[-0.02em] sm:text-3xl" style={{ color: GOLD, fontFamily: FONT }}>
            in four steps.
          </p>
        </Reveal>

        <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => (
            <motion.div key={s.n} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.6, ease: EASE }} className="relative">
              <motion.div className="text-6xl font-black tabular-nums" style={{ color: s.color, fontFamily: FONT, letterSpacing: "-0.04em" }}
                initial={{ scale: 0.85 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.1 + 0.15, type: "spring", stiffness: 200, damping: 15 }}>
                {s.n}
              </motion.div>
              <h3 className="mt-4 text-lg font-bold" style={{ color: INK, fontFamily: FONT, letterSpacing: "-0.02em" }}>{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed font-medium" style={{ color: "#666", fontFamily: FONT }}>{s.desc}</p>
              {i < steps.length - 1 && (
                <div className="mt-6 hidden h-px w-12 lg:block" style={{ background: `${s.color}20` }} />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════
// CTA — jet black, gold accents, gold corner decorations
// ═══════════════════════════════════════════════════════════════
function CTASection() {
  return (
    <section className="relative overflow-hidden py-28 sm:py-36" style={{ background: WINE }}>
      {/* Grain texture */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`, backgroundSize: "200px 200px" }} />
      {/* Sand glow */}
      <motion.div animate={{ scale: [1, 1.2, 1], opacity: [0.08, 0.15, 0.08] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute left-1/2 top-1/2 h-[400px] w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{ background: `radial-gradient(circle, ${GOLD}30 0%, transparent 60%)` }} />
      {/* Top + bottom ruled bands */}
      <div className="absolute top-0 left-0 right-0 h-2" style={{ background: GOLD }} />
      <div className="absolute bottom-0 left-0 right-0 h-2" style={{ background: GOLD }} />

      {/* Sand corner decorations */}
      <div className="relative mx-auto max-w-3xl px-6 text-center">
        <div className="relative p-12 sm:p-16" style={{ border: `1.5px solid ${GOLD}40` }}>
          <GoldCorner position="tl" />
          <GoldCorner position="tr" />
          <GoldCorner position="bl" />
          <GoldCorner position="br" />

          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease: EASE }}>
            <div className="mb-8 flex items-center justify-center gap-4">
              <div className="h-px w-12" style={{ background: GOLD }} />
              <SparkleIcon className="h-5 w-5" accent={GOLD} style={{ color: GOLD }} />
              <div className="h-px w-12" style={{ background: GOLD }} />
            </div>
            <h2 className="text-4xl font-black tracking-[-0.035em] text-white sm:text-5xl lg:text-6xl" style={{ fontFamily: FONT }}>
              Ready to simplify your
            </h2>
            <h2 className="mt-1 text-4xl font-black tracking-[-0.035em] sm:text-5xl lg:text-6xl" style={{ fontFamily: FONT, color: GOLD, fontStyle: "italic" }}>
              child's education?
            </h2>
            <p className="mt-6 text-lg font-medium" style={{ color: "rgba(255,255,255,0.55)", fontFamily: FONT, fontStyle: "italic" }}>Create your free parent account today.</p>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button asChild size="lg" className="h-12 rounded-none px-8 text-[13px] font-bold uppercase tracking-[0.15em] transition-all hover:opacity-90" style={{ background: GOLD, color: INK, border: "none", fontFamily: FONT_SANS }}>
                <Link to="/auth">Get Started <ArrowRightIcon className="ml-2 h-4 w-4" accent={INK} style={{ color: INK }} /></Link>
              </Button>
              <Button asChild variant="ghost" size="lg" className="h-12 rounded-none px-7 text-[13px] font-bold uppercase tracking-[0.15em] text-white hover:bg-white/10" style={{ border: `1.5px solid ${GOLD}50`, fontFamily: FONT_SANS }}>
                <Link to="/ecosystem">Explore Ecosystem</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default ParentsPage;
